import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Download, FileSpreadsheet, Users, Swords, Trophy, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminExport() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState({});

  const handleExport = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    
    try {
      let url = `${API_URL}/api/export/${type}`;
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename=(.+)/);
      const instanceName = process.env.REACT_APP_INSTANCE_NAME || 'padel';
      const filename = filenameMatch ? filenameMatch[1] : `${instanceName}_padel_${type}.csv`;

      // Download the file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(language === 'el' ? 'Η εξαγωγή ολοκληρώθηκε' : 'Export completed');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'el' ? 'Σφάλμα κατά την εξαγωγή' : 'Export failed');
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const exportOptions = [
    {
      type: 'players',
      icon: Users,
      title: t('exportPlayers'),
      description: language === 'el' 
        ? 'ID, Όνομα, Επώνυμο, Email, Τηλέφωνο' 
        : 'ID, First Name, Last Name, Email, Phone',
      color: 'text-blue-500'
    },
    {
      type: 'matches',
      icon: Swords,
      title: t('exportMatches'),
      description: language === 'el'
        ? 'ID, Ημ/νία, Παίκτες, Σκορ, Νικητής'
        : 'ID, Date, Players, Scores, Winner',
      color: 'text-green-500'
    },
    {
      type: 'leaderboard',
      icon: Trophy,
      title: t('exportLeaderboard'),
      description: language === 'el'
        ? 'ID, Κατάταξη, Πόντοι, Αγώνες, Νίκες'
        : 'ID, Rank, Points, Matches, Wins',
      color: 'text-amber-500'
    }
  ];

  return (
    <div className="p-6 max-w-4xl" data-testid="admin-export">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-foreground mb-1 flex items-center gap-3">
          <FileSpreadsheet className="h-7 w-7" />
          {t('exportData')}
        </h1>
        <p className="text-muted-foreground">
          {t('exportDescription')}
        </p>
      </div>

      {/* Date Filters */}
      <div className="bg-card rounded-xl border border-border p-6 mb-6">
        <h2 className="font-medium text-foreground mb-4">
          {language === 'el' ? 'Φίλτρα ημερομηνίας (προαιρετικά)' : 'Date filters (optional)'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="fromDate">{t('fromDate')}</Label>
            <Input
              id="fromDate"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="from-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="toDate">{t('toDate')}</Label>
            <Input
              id="toDate"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="to-date-input"
            />
          </div>
        </div>
        {(fromDate || toDate) && (
          <button
            onClick={() => { setFromDate(''); setToDate(''); }}
            className="mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {language === 'el' ? 'Καθαρισμός φίλτρων' : 'Clear filters'}
          </button>
        )}
      </div>

      {/* Export Options */}
      <div className="grid gap-4 mb-6">
        {exportOptions.map(({ type, icon: Icon, title, description, color }) => (
          <div 
            key={type}
            className="bg-card rounded-xl border border-border p-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg bg-muted ${color}`}>
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">{title}</h3>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            </div>
            <Button
              onClick={() => handleExport(type)}
              disabled={loading[type]}
              variant="outline"
              data-testid={`export-${type}-btn`}
            >
              {loading[type] ? (
                <span className="animate-pulse">{language === 'el' ? 'Εξαγωγή...' : 'Exporting...'}</span>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  {t('downloadCsv')}
                </>
              )}
            </Button>
          </div>
        ))}
      </div>

      {/* Export All */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border border-primary/20 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/20 text-primary">
              <Package className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">{t('exportAll')}</h3>
              <p className="text-sm text-muted-foreground">
                {language === 'el' 
                  ? 'Λήψη όλων των δεδομένων σε ένα ZIP αρχείο (3 CSVs)'
                  : 'Download all data in a single ZIP file (3 CSVs)'}
              </p>
            </div>
          </div>
          <Button
            onClick={() => handleExport('all')}
            disabled={loading.all}
            data-testid="export-all-btn"
          >
            {loading.all ? (
              <span className="animate-pulse">{language === 'el' ? 'Εξαγωγή...' : 'Exporting...'}</span>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                {t('downloadZip')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
